import React from "react";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import * as CmsHttp from "@app/http/cms";
import { GMAP_KEY, META_DESCRIPTION, META_TITLE, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import { sliceMetaDescription } from "@app/utilities/helpers";
import { Geolocation } from "@models/Geolocation";
import GoogleMapComp from "@components/maps/GoogleMapComp";
import SimpleExpertLinkCard from "@components/experts/SimpleExpertLinkCard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import { CmsMeta } from "@app/http/cms/components/CmsMeta";

interface ILocationDetailPage {
  geolocation: Geolocation;
}

const LocationDetailPage: NextPage<ILocationDetailPage> = (props: ILocationDetailPage) => {
  const { geolocation } = props,
    pageName = geolocation.name,
    ssrMetaDescription = sliceMetaDescription(geolocation.content.text, 210),
    googleMapUrl = `https://maps.googleapis.com/maps/api/js?v=3&key=${GMAP_KEY}`;

  const officeName = [geolocation.name, "Location"].join(" "),
    addressCityState = [geolocation?.city, [geolocation?.state, geolocation?.zip].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    carouselImages = geolocation.carousels,
    officeHours = geolocation.operatingHours;

  /**
   * Meta contents
   */
  let metaTitle = META_TITLE || "",
    metaDescription = META_DESCRIPTION || "",
    metaUrl = META_URL || "",
    metaOgImg = META_URL + `/logo.png`,
    metaTwitterImg = META_URL + `/logo.png`;

  if (geolocation) {
    metaTitle = `${officeName} | Heritage Counseling`;
    metaDescription = ssrMetaDescription || metaDescription;
    metaUrl = `${META_URL}/locations/${geolocation.slug}`;
    metaOgImg = geolocation.image?.url || metaOgImg;
    metaTwitterImg = geolocation.image?.url || metaTwitterImg;
  }

  // Override metas using pageMeta
  const pageMeta = (geolocation?.meta || {}) as CmsMeta;
  if (Object.keys(pageMeta).length > 0) {
    metaTitle = pageMeta?.title || metaTitle;
    metaDescription = pageMeta?.description || metaDescription;
    metaOgImg = pageMeta?.ogImage?.url || metaOgImg;
    metaTwitterImg = pageMeta?.twitterImage?.url || metaTwitterImg;
  }

  return (
    <PageLayoutV1 title={pageName}>
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaOgImg} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaTwitterImg} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
        {geolocation?.schema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(geolocation.schema),
            }}
          ></script>
        )}
      </Head>
      <div className="location-detail-page" data-testid="location-detail-page">
        <nav className="mb-5 mb-lg-10" role="navigation">
          <Link href="/locations">
            <a className="link-back">
              <i className="fa fa-angle-left"></i>
              Back to Locations
            </a>
          </Link>
        </nav>
        {geolocation ? (
          <div className="wrapper">
            <div className="horizontal-hid-layout-hoc">
              <GoogleMapComp
                coordinate={geolocation.coordinate}
                googleMapURL={googleMapUrl}
                loadingElement={<div style={{ height: `100%` }} />}
                containerElement={<div className="map-container" />}
                mapElement={<div className="map" />}
                mapProps={{
                  defaultOptions: {
                    disableDefaultUI: true,
                  },
                }}
              />
              <div className="content">
                <h3>{officeName}</h3>
                <address>
                  {geolocation.address.map((address, index) => (
                    <div key={index}>
                      <span className="address-line">{address}</span>
                    </div>
                  ))}
                  <div>
                    <span className="address-city-state">{addressCityState}</span>
                  </div>
                </address>
                {geolocation.contact?.phone && (
                  <a title="Phone" aria-label="Phone" href={`tel:${geolocation.contact.phone}`} className="btn btn-external btn-main px-5 py-3" rel="noopener noreferrer">
                    <i className="fa fa-phone-alt mr-2"></i>
                    <span>Call for more information</span>
                  </a>
                )}
              </div>
            </div>
            {!!carouselImages.length && (
              <Swiper navigation={true} loop={true} modules={[Navigation]} className="mySwiper">
                {carouselImages.map((carouselItem, idx) => {
                  return (
                    <SwiperSlide key={idx}>
                      <img src={carouselItem.photo.url} alt={carouselItem.caption} width={carouselItem.photo.width} height={carouselItem.photo.height} />
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}
            {!!officeHours.length && (
              <section className="business-hours">
                <label className="title">Business Hours</label>
                <ul className="list-group list-group-flush business-hours-list">
                  {officeHours.map((officeHour, idx) => {
                    return (
                      <li className="list-group-item" key={idx}>
                        <strong>{officeHour.dayName}</strong>
                        <span>{officeHour.operatingHour}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
            {!!geolocation?.content?.text && <div className="cms-content" dangerouslySetInnerHTML={{ __html: geolocation.content.html }} />}
            {!!geolocation.experts?.length && (
              <section className="experts">
                <label className="title">Experts</label>
                <div className="experts-wrapper">
                  {geolocation.experts.map((expert, idx) => {
                    return <SimpleExpertLinkCard expert={expert} key={idx} />;
                  })}
                </div>
              </section>
            )}
          </div>
        ) : (
          <p className="text-center">No locations found</p>
        )}
        <nav className="mt-5 mt-lg-10" role="navigation">
          <Link href="/locations">
            <a className="link-back">
              <i className="fa fa-angle-left"></i>
              Back to Locations
            </a>
          </Link>
        </nav>
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = (params && Array.isArray(params?.slug) ? params.slug[0] : params?.slug) as string,
    ssrGeolocation = await CmsHttp.getGeolocationBySlug(slug);

  return {
    props: {
      geolocation: ssrGeolocation || null,
      slug,
    } as ILocationDetailPage,
    notFound: !ssrGeolocation,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const models = await CmsHttp.getGeolocations(),
    slugs = models.map((model) => model?.slug || "").filter(Boolean);

  return {
    paths: slugs.map((slug) => ({
      params: {
        slug,
      },
    })),
    fallback: "blocking",
  };
};

export default LocationDetailPage;
